import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ServerSidePagination Component
 * 
 * Displays server-side pagination controls with:
 * - Rows per page dropdown (10, 20, 50)
 * - Page navigation (prev, page numbers, next)
 * - Go to page input
 * 
 * Used when total_count > 30
 */
const ServerSidePagination = ({
  totalCount,
  currentPage,
  rowsPerPage,
  isLoading,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const { isDarkMode } = useDarkMode();
  const [goToPageValue, setGoToPageValue] = useState('');

  const totalPages = useMemo(() => 
    Math.ceil(totalCount / rowsPerPage), 
    [totalCount, rowsPerPage]
  );

  // Reset go to page input when page changes
  useEffect(() => {
    setGoToPageValue('');
  }, [currentPage]);

  // Generate visible page numbers
  const getVisiblePages = useCallback(() => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust range if near start or end
      if (currentPage <= 3) {
        endPage = Math.min(4, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis before middle pages if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after middle pages if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPageValue, 10);
    if (pageNum >= 1 && pageNum <= totalPages && pageNum !== currentPage && !isLoading) {
      onPageChange(pageNum);
    }
    setGoToPageValue('');
  };

  const handleGoToPageKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleGoToPage(e);
    }
  };

  const rowsPerPageOptions = [10, 20, 50];
  const visiblePages = getVisiblePages();

  return (
    <div className={`flex flex-wrap items-center justify-between gap-4 px-4 py-3 border-t ${
      isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'
    }`}>
      {/* Left side: Rows per page */}
      <div className="flex items-center gap-2">
        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
          Rows per page:
        </span>
        <div className="relative">
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            disabled={isLoading}
            className={`appearance-none px-3 py-1.5 pr-8 rounded-md text-sm font-medium border cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              isDarkMode 
                ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600 focus:ring-blue-500 focus:ring-offset-slate-800' 
                : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-blue-500 focus:ring-offset-white'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <div className={`absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none ${
            isDarkMode ? 'text-slate-400' : 'text-gray-500'
          }`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Center: Pagination controls */}
      <div className="flex items-center gap-1">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className={`p-1.5 rounded-md transition-colors ${
            currentPage === 1 || isLoading
              ? (isDarkMode 
                  ? 'text-slate-600 cursor-not-allowed' 
                  : 'text-gray-300 cursor-not-allowed')
              : (isDarkMode 
                  ? 'text-slate-300 hover:bg-slate-700 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900')
          }`}
          title="Previous page"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {visiblePages.map((page, index) => (
            page === '...' ? (
              <span 
                key={`ellipsis-${index}`} 
                className={`px-2 py-1 text-sm ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`}
              >
                …
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                disabled={isLoading}
                className={`min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors ${
                  page === currentPage
                    ? (isDarkMode 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-600 text-white')
                    : (isDarkMode 
                        ? 'text-slate-300 hover:bg-slate-700 hover:text-white' 
                        : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900')
                } ${isLoading ? 'cursor-not-allowed' : ''}`}
              >
                {page}
              </button>
            )
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className={`p-1.5 rounded-md transition-colors ${
            currentPage === totalPages || isLoading
              ? (isDarkMode 
                  ? 'text-slate-600 cursor-not-allowed' 
                  : 'text-gray-300 cursor-not-allowed')
              : (isDarkMode 
                  ? 'text-slate-300 hover:bg-slate-700 hover:text-white' 
                  : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900')
          }`}
          title="Next page"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Right side: Go to page */}
      <div className="flex items-center gap-2">
        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
          Go to:
        </span>
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={1}
            max={totalPages}
            value={goToPageValue}
            onChange={(e) => setGoToPageValue(e.target.value)}
            onKeyDown={handleGoToPageKeyDown}
            placeholder=""
            disabled={isLoading}
            className={`w-14 px-2 py-1.5 rounded-md text-sm text-center border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              isDarkMode 
                ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500 focus:ring-offset-slate-800' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:ring-offset-white'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
        </div>
      </div>
    </div>
  );
};

ServerSidePagination.propTypes = {
  totalCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

ServerSidePagination.defaultProps = {
  isLoading: false,
};

export default ServerSidePagination;
